import React, { useMemo, useState } from "react";

import { formatDate, formatDatePrecise } from "../constants";
import {
  accessReason,
  describeExplanation,
  effectiveAccess,
  humanizeRoleType,
  needsAttention,
  subjectKey,
} from "../people";
import { InfoDot } from "./InfoTip";

const OUTCOME = { FOLLOW: "follow", ALL: "all", OWN: "own" };
const isEmailLike = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isAddyStaff = (subject) => {
  const email = String(subject.email || "").toLowerCase();

  return email.endsWith("@addy-ai.com") || email.endsWith("@addy.com");
};

function selectionFor(subject, outcome) {
  if (outcome === OUTCOME.ALL) {
    return subject.assignableAdminRole;
  }
  if (outcome === OUTCOME.OWN) {
    return subject.scopedSelectionValue;
  }

  return null;
}

// An ops decision outranks the reading, so the note says what it rests on
// instead of warning that LendingPad could undo it.
function opsDecisionNote(subject) {
  const { capture } = subject;

  if (!capture.hasReading) {
    return ", decided without a LendingPad reading";
  }
  if (subject.opsOutcome === OUTCOME.ALL && !capture.roleTypes.includes(subject.opsRoleSelection)) {
    return ", although LendingPad gives them no admin role";
  }

  return "";
}

function formatCount(value) {
  if (typeof value !== "number") {
    return "—";
  }

  return value.toLocaleString();
}

function AccessPill({ access }) {
  return access === "all" ? (
    <span className="integrations-people-pill all">Every imported loan</span>
  ) : (
    <span className="integrations-people-pill own">Only loans they are on</span>
  );
}

/**
 * The addresses Addy treats as this person besides their login: one chip per
 * address, and a field to link another. Linking is what makes a loan that
 * LendingPad filed under a different address open for them.
 */
function LinkedAddresses({ subject, disabled, busy, onLink, onUnlink }) {
  const [typed, setTyped] = useState("");
  const losEmails = subject.losEmails || [];
  const typedEmail = typed.trim().toLowerCase();
  const isKnown =
    typedEmail === String(subject.email || "").toLowerCase() ||
    losEmails.includes(typedEmail);
  const canLink = isEmailLike(typedEmail) && !isKnown && !disabled && !busy;

  const link = () => {
    if (!canLink) {
      return;
    }

    onLink(subject, typedEmail);
    setTyped("");
  };

  return (
    <div className="integrations-aka">
      <span>
        <b>Also known as:</b>{" "}
        {losEmails.length > 0
          ? losEmails.map((email) => (
              <span key={email} className="integrations-aka-chip">
                {email}
                <button
                  type="button"
                  className="integrations-aka-remove"
                  aria-label={`Unlink ${email}`}
                  disabled={disabled || busy}
                  onClick={() => onUnlink(subject, email)}
                >
                  ×
                </button>
              </span>
            ))
          : "no other address"}
      </span>
      <div className="integrations-aka-add">
        <input
          type="email"
          className="integrations-input"
          placeholder="address as it appears in LendingPad"
          aria-label={`LendingPad address for ${subject.name || subject.email}`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              link();
            }
          }}
          disabled={disabled || busy}
        />
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary integrations-btn-small"
          disabled={!canLink}
          onClick={link}
        >
          Link
        </button>
      </div>
      <span className="integrations-people-note">
        Linking grants access to loans that carry the address. Every change is logged.
      </span>
    </div>
  );
}

function PersonRow({
  subject,
  disabled,
  busy,
  onSelect,
  onClear,
  onLinkLosEmail,
  onUnlinkLosEmail,
}) {
  const [showWhy, setShowWhy] = useState(false);
  const { capture } = subject;
  const access = effectiveAccess(subject);
  const reason = accessReason(subject);
  // A decision can precede any reading, so the only thing that truly blocks
  // this row is a reading that belongs to somebody else.
  const canManage = capture.ownerUidMatches !== false;
  const noLoans = subject.loansOn === 0;

  return (
    <div className="integrations-people-row">
      <div className="integrations-people-who">
        <div className="integrations-people-name">
          {subject.name || subject.email || "Unknown"}
          {subject.subjectType === "owner" ? (
            <span className="integrations-people-tag">Owner</span>
          ) : null}
        </div>
        <div className="integrations-people-sub">
          {subject.email}
          {subject.addyRole ? ` · ${subject.addyRole}` : ""}
        </div>
      </div>

      <div className={`integrations-people-count ${noLoans ? "is-zero" : ""}`}>
        {formatCount(subject.loansOn)}
      </div>

      <div className="integrations-people-count">
        {formatCount(subject.loansCanSee)}
      </div>

      {/* The reason is spelled out only when it asks for something. A row that
          simply follows LendingPad says so in "Why?", not on every line. */}
      <div className="integrations-people-cell">
        <AccessPill access={access} />
      </div>

      <div className="integrations-people-cell">
        {canManage ? (
          <select
            className="integrations-select"
            value={subject.opsOutcome || OUTCOME.FOLLOW}
            disabled={disabled || busy}
            aria-label={`What ${subject.name || subject.email} can open`}
            onChange={(event) =>
              onSelect(subject, selectionFor(subject, event.target.value))
            }
          >
            <option value={OUTCOME.FOLLOW}>Follow LendingPad</option>
            <option value={OUTCOME.ALL}>Every imported loan</option>
            <option value={OUTCOME.OWN}>Only loans they are on</option>
          </select>
        ) : (
          <select className="integrations-select" disabled value="none">
            <option value="none">Cannot be changed</option>
          </select>
        )}
        <button
          type="button"
          className="integrations-vis-link"
          aria-expanded={showWhy}
          onClick={() => setShowWhy((shown) => !shown)}
        >
          {showWhy ? "Hide why" : "Why?"}
        </button>
      </div>

      {showWhy ? (
        <div className="integrations-people-why">
          <span>
            <b>Roles in LendingPad:</b>{" "}
            {capture.roleTypes.length > 0
              ? capture.roleTypes.map((roleType) => (
                  <span key={roleType} className="integrations-vis-chip">
                    {humanizeRoleType(roleType)}
                  </span>
                ))
              : "none read yet"}
            {capture.capturedAt ? (
              <span title={formatDatePrecise(capture.capturedAt)}>
                {" "}
                · last seen {formatDate(capture.capturedAt)}
              </span>
            ) : null}
          </span>
          <span>
            <b>Decided by:</b> {reason.text}
          </span>
          <span>
            <b>Role in Addy:</b> {subject.addyRole || "none"}
            {subject.addyViewScope === "own"
              ? " (own loans only; change it on the team page)"
              : subject.addyViewScope === "all"
                ? " (can open every loan)"
                : ""}
          </span>
          {subject.opsRoleSelection ? (
            <span>
              <b>Ops decision:</b>{" "}
              {subject.opsOutcome === OUTCOME.ALL ? "every imported loan" : "only loans they are on"}
              {opsDecisionNote(subject)}
            </span>
          ) : null}
          <LinkedAddresses
            subject={subject}
            disabled={disabled}
            busy={busy}
            onLink={onLinkLosEmail}
            onUnlink={onUnlinkLosEmail}
          />
          {capture.present ? (
            <span>
              <button
                type="button"
                className="integrations-vis-forget"
                disabled={disabled || busy}
                onClick={() => onClear(subject)}
              >
                Forget LendingPad roles
              </button>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DeclaredRow({
  entry,
  subjects,
  isFiltering,
  disabled,
  busy,
  onRemove,
  onLink,
  onUnlink,
}) {
  const noLoans = entry.loansOn === 0;
  const linkedTo = entry.linkedTo
    ? subjects.find(
        (subject) =>
          subject.subjectType === entry.linkedTo.subjectType &&
          (subject.memberId || null) === (entry.linkedTo.memberId || null),
      ) || null
    : null;
  const linkedKey = linkedTo ? subjectKey(linkedTo) : "";

  // "Belongs to" writes the same link the person's own row offers, from the
  // side operations usually start on: the address with loans and no owner.
  const changeOwner = (nextKey) => {
    if (nextKey === linkedKey) {
      return;
    }
    if (!nextKey && linkedTo) {
      onUnlink(linkedTo, entry.email);

      return;
    }

    const next = subjects.find((subject) => subjectKey(subject) === nextKey);
    if (next) {
      onLink(next, entry.email);
    }
  };

  return (
    <div className="integrations-people-row">
      <div className="integrations-people-who">
        <div className="integrations-people-name">{entry.email}</div>
        {linkedTo ? (
          <div className="integrations-people-sub">
            counts as {linkedTo.name || linkedTo.email}
          </div>
        ) : null}
      </div>

      {/* A declared address on zero loans is the silent-typo case; the amber
          count carries the hint so the row itself stays quiet. */}
      <div
        className={`integrations-people-count ${noLoans ? "is-zero" : ""}`}
        title={
          noLoans && isFiltering
            ? "Not seen on any loan yet. Check the spelling in LendingPad."
            : undefined
        }
      >
        {formatCount(entry.loansOn)}
      </div>

      <div className="integrations-people-count">—</div>

      <div className="integrations-people-cell">
        <span className="integrations-people-note">
          {linkedTo ? "Opens as the linked user" : "No Addy account"}
        </span>
      </div>

      <div className="integrations-people-cell">
        <select
          className="integrations-select"
          value={linkedKey}
          disabled={disabled || busy}
          aria-label={`Who ${entry.email} belongs to`}
          onChange={(event) => changeOwner(event.target.value)}
        >
          <option value="">Belongs to: nobody</option>
          {subjects.map((subject) => (
            <option key={subjectKey(subject)} value={subjectKey(subject)}>
              Belongs to: {subject.name || subject.email}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="integrations-chip-btn"
          disabled={disabled || busy}
          onClick={() => onRemove(entry.email)}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

/**
 * "Check access": the question that used to take three tabs and the customer's
 * team page. One person, one loan, the chain that decides, and what to do.
 */
function CheckAccess({ subjects, onExplain }) {
  const [subjectId, setSubjectId] = useState(() =>
    subjects[0] ? subjectKey(subjects[0]) : "",
  );
  const [loan, setLoan] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const subject = subjects.find((entry) => subjectKey(entry) === subjectId) || subjects[0];
  const canCheck = Boolean(subject) && loan.trim().length > 0 && !checking;

  const check = async () => {
    if (!canCheck) {
      return;
    }

    setChecking(true);
    setError(null);
    try {
      const explanation = await onExplain({
        subjectType: subject.subjectType,
        memberId: subject.memberId || null,
        loan: loan.trim(),
      });
      setResult({ subject, explanation });
    } catch (checkError) {
      setResult(null);
      setError(checkError?.message || "Could not check access");
    } finally {
      setChecking(false);
    }
  };

  const described = result ? describeExplanation(result.explanation) : null;

  return (
    <div className="integrations-access-check">
      <div className="integrations-access-check-head">
        <b>Check access</b>
        <span>Why someone can or cannot open a loan</span>
      </div>
      <div className="integrations-access-check-row">
        <select
          className="integrations-select"
          aria-label="Person to check"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        >
          {subjects.map((entry) => (
            <option key={subjectKey(entry)} value={subjectKey(entry)}>
              {entry.name || entry.email}
            </option>
          ))}
        </select>
        <span className="integrations-people-note">opening</span>
        <input
          className="integrations-input"
          placeholder="Loan number, Addy loan id or LendingPad id"
          aria-label="Loan to check"
          value={loan}
          onChange={(event) => setLoan(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              check();
            }
          }}
        />
        <button
          type="button"
          className={`integrations-btn integrations-btn-secondary ${checking ? "is-loading" : ""}`}
          disabled={!canCheck}
          onClick={check}
        >
          {checking ? "Checking…" : "Check"}
        </button>
      </div>

      {error ? <div className="integrations-result err">{error}</div> : null}

      {described ? (
        <div className="integrations-access-check-out">
          <div className="integrations-chain">
            {described.steps.map((step, index) => (
              <span key={`${step.label}-${index}`} className="integrations-chain-item">
                {index > 0 ? (
                  <span className="integrations-chain-arrow" aria-hidden="true">
                    →
                  </span>
                ) : null}
                <span className={`integrations-chain-step ${step.tone}`}>{step.label}</span>
              </span>
            ))}
          </div>
          <b>{described.verdict}</b>
          {described.fix ? <span> {described.fix}</span> : null}
          {result.explanation?.found ? (
            <div className="integrations-people-note" style={{ marginTop: 4 }}>
              {result.explanation.loan.loanNumber
                ? `Loan ${result.explanation.loan.loanNumber}`
                : `Loan ${result.explanation.loan.id}`}
              {result.explanation.loan.stage ? ` · ${result.explanation.loan.stage}` : ""}
              {" · LendingPad team: "}
              {result.explanation.loan.teamEmails.length > 0
                ? result.explanation.loan.teamEmails.join(", ")
                : "nobody recorded"}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "People": everyone who matters to the integration, one row each, answering
 * the two questions that used to live on two tabs — do this person's loans
 * count for import, and what can they open once a loan is in.
 *
 * Addy users are matched automatically and carry a visibility verdict.
 * Declared addresses are LendingPad identities with no Addy seat: their loans
 * are imported for the team, and they have nothing to open. Adding or removing
 * one applies immediately (after a confirmation in the panel), like the access
 * select does, so the tab has one saving model rather than two.
 */
export function LendingPadPeopleTab({
  people,
  loading,
  error,
  busyKey,
  disabled,
  onSelect,
  onClear,
  onAddAddress,
  onRemoveAddress,
  onLinkLosEmail,
  onUnlinkLosEmail,
  onRetry,
  onExplain,
}) {
  const [search, setSearch] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [typed, setTyped] = useState("");

  // Addy staff sit in customer organizations for support; they are never who
  // operations came to look at, so they always sort last.
  // The owner leads the list whoever they are; Addy support accounts among
  // the members sink to the bottom so the customer's own team reads first.
  const subjects = useMemo(() => {
    if (!people) {
      return [];
    }

    const members = people.members || [];

    return [
      people.owner,
      ...members.filter((subject) => !isAddyStaff(subject)),
      ...members.filter(isAddyStaff),
    ].filter(Boolean);
  }, [people]);
  const declared = useMemo(() => people?.declared || [], [people]);
  const isFiltering = people?.importScope?.isFiltering !== false;

  const counts = useMemo(
    () => ({
      total: subjects.length + declared.length,
      all: subjects.filter((subject) => effectiveAccess(subject) === "all").length,
      attention:
        subjects.filter(needsAttention).length +
        (isFiltering ? declared.filter((entry) => entry.loansOn === 0).length : 0),
    }),
    [subjects, declared, isFiltering],
  );

  const term = search.trim().toLowerCase();
  const matches = (text) => !term || text.toLowerCase().includes(term);
  const visibleSubjects = subjects.filter(
    (subject) =>
      matches(`${subject.name || ""} ${subject.email || ""}`) &&
      (!attentionOnly || needsAttention(subject)),
  );
  const visibleDeclared = declared.filter(
    (entry) =>
      matches(entry.email) &&
      (!attentionOnly || (isFiltering && entry.loansOn === 0)),
  );

  const typedEmail = typed.trim().toLowerCase();
  const isKnown =
    declared.some((entry) => entry.email === typedEmail) ||
    subjects.some((subject) => (subject.email || "").toLowerCase() === typedEmail);
  const canAdd = isEmailLike(typedEmail) && !isKnown && !disabled;

  const addTyped = () => {
    if (!canAdd) {
      return;
    }

    onAddAddress(typedEmail);
    setTyped("");
  };

  if (loading) {
    return <div className="integrations-empty">Loading…</div>;
  }

  if (error) {
    // `fetchWithFallback` throws on any non-2xx, so an environment whose backend
    // predates these endpoints arrives here as a 404. Expected during rollout,
    // so it gets an inline notice rather than a toast, and the tab stays put.
    const looksUnavailable = /404|not found/i.test(error);

    return (
      <div className="integrations-result err">
        {looksUnavailable
          ? "This is not available on this environment yet. It needs the backend release that adds it."
          : error}
        {onRetry && !looksUnavailable ? (
          <button
            type="button"
            className="integrations-btn integrations-btn-ghost integrations-btn-small"
            style={{ marginLeft: 10 }}
            onClick={onRetry}
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (!people) {
    return null;
  }

  return (
    <div className="integrations-people">
      {!isFiltering ? (
        <div className="integrations-save-note warn">
          <span className="integrations-note-mark" aria-hidden="true">
            !
          </span>
          <span>
            This organization imports every loan, so declared people decide
            nothing right now. They are kept for when the scope is narrowed
            back in <b>Import policy</b>.
          </span>
        </div>
      ) : null}

      {!people.organization?.lendingPadEnabled ? (
        <div className="integrations-result">
          LendingPad is not enabled for this organization, so nothing here is
          limiting anyone yet.
        </div>
      ) : null}

      <div className="integrations-vis-summary">
        <div className="integrations-vis-cell">
          <b>{counts.total}</b>
          <span>people</span>
        </div>
        <div className="integrations-vis-cell ok">
          <b>{counts.all}</b>
          <span>open every loan</span>
        </div>
        <div className="integrations-vis-cell warn">
          <b>{counts.attention}</b>
          <span>need attention</span>
        </div>
      </div>

      <div className="integrations-vis-toolbar">
        <input
          type="search"
          className="integrations-input"
          placeholder="Search by name or email"
          aria-label="Search for a person"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <button
          type="button"
          className="integrations-chip-btn"
          aria-pressed={attentionOnly}
          onClick={() => setAttentionOnly((on) => !on)}
        >
          Only who needs attention
        </button>
      </div>

      <div className="integrations-people-table">
        <div className="integrations-people-head">
          <span>Person</span>
          <span>
            Loans
            <InfoDot
              summary="How many loans in Addy this person is on."
              detail="A loan is imported when someone listed here is on its LendingPad team, so this is also the count that person's loans contribute."
            />
          </span>
          <span>
            Can see
            <InfoDot
              summary="How many loans this person can actually open today."
              detail="Every imported loan, or only the ones they're on — whichever &quot;Can open&quot; resolves to, counted, including the Addy role's narrowing. The two columns can never disagree."
            />
          </span>
          <span>
            Can open
            <InfoDot
              summary="What this person opens on the loan board today."
              detail="Their LendingPad roles decide, unless the ops team has set it here. Their Addy role can only narrow it further, and decides alone when LendingPad has no reading."
            />
          </span>
          <span>Change</span>
        </div>

        <div className="integrations-people-group">
          Addy users<span>loans they are on are imported</span>
        </div>
        {visibleSubjects.map((subject) => (
          <PersonRow
            key={subjectKey(subject)}
            subject={subject}
            disabled={disabled}
            busy={busyKey === subjectKey(subject)}
            onSelect={onSelect}
            onClear={onClear}
            onLinkLosEmail={onLinkLosEmail}
            onUnlinkLosEmail={onUnlinkLosEmail}
          />
        ))}
        {visibleSubjects.length === 0 ? (
          <div className="integrations-people-empty">
            {subjects.length === 0 ? "Nobody in this organization yet." : "Nobody matches that."}
          </div>
        ) : null}

        <div className="integrations-people-group">
          Declared LendingPad addresses<span>no Addy seat; loans they are on are imported</span>
        </div>
        {visibleDeclared.map((entry) => (
          <DeclaredRow
            key={entry.email}
            entry={entry}
            subjects={subjects}
            isFiltering={isFiltering}
            disabled={disabled}
            busy={busyKey === `declared:${entry.email}`}
            onRemove={onRemoveAddress}
            onLink={onLinkLosEmail}
            onUnlink={onUnlinkLosEmail}
          />
        ))}
        {visibleDeclared.length === 0 ? (
          <div className="integrations-people-empty">
            {declared.length === 0 ? "None declared." : "Nobody matches that."}
          </div>
        ) : null}
      </div>

      <div className="integrations-roster-add">
        <input
          type="email"
          className="integrations-input"
          placeholder="LendingPad address without an Addy account"
          aria-label="LendingPad address to add"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addTyped();
            }
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={addTyped}
          disabled={!canAdd}
        >
          Add person
        </button>
      </div>
      {isKnown && typedEmail ? (
        <p className="integrations-field-hint">Already in the list.</p>
      ) : null}

      {subjects.length > 0 ? <CheckAccess subjects={subjects} onExplain={onExplain} /> : null}
    </div>
  );
}

export { OUTCOME };
